import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { img } from '@/lib/images';
import CheckInOutNote from '@/components/feature/CheckInOutNote';
import { money } from '@/lib/currency';
import { useExchangeRate } from '@/hooks/usePrices';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

const BALI_CHAKRA_LOGO = img('about/logo-bali-chakra-tour_01.webp');

interface VoucherData {
  id: string;
  booking_no?: string | null;
  lang?: string;
  villa_slug: string;
  villa_name: string;
  guest_name: string;
  email: string;
  phone: string | null;
  rooms: number;
  check_in: string;
  check_out: string;
  check_in_display: string;
  check_out_display: string;
  nights: number;
  adults: number;
  price_per_night: number;
  total_price: number;
  status: string;
  error?: string;
}

const PRINT_CSS = `
  @page { size: A4; margin: 3cm 0 0 0; }
  @media print {
    body { margin: 0 !important; padding: 0 !important; }
    .voucher-page { padding: 0 !important; min-height: 0 !important; background: #fff !important; }
    .voucher-sheet { font-size: 11px; }
    .vh-header { padding: 10px 28px !important; }
    .vh-header-gap { gap: 6px !important; }
    .vh-logo { width: 200px !important; }
    .vh-header h1 { font-size: 20px !important; }
    .vh-title-block { padding: 10px 3cm 4px !important; }
    .vh-title-block h2 { font-size: 17px !important; }
    .vh-body { padding: 0 3cm 8px !important; }
    .vh-body > p { margin-bottom: 6px !important; }
    .vh-guest { padding: 7px 12px !important; margin-bottom: 6px !important; }
    .vh-table td { padding-top: 3px !important; padding-bottom: 3px !important; font-size: 11px !important; }
    .vh-footer { padding: 8px 3cm !important; font-size: 10px !important; line-height: 1.4 !important; }
    .vh-footer p { margin-bottom: 4px !important; }
  }
`;

export default function VoucherPage() {
  useExchangeRate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id') || searchParams.get('voucher');
  const [data, setData] = useState<VoucherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Ungültiger Bestätigungs-Link: Keine Buchungs-ID angegeben.');
      setLoading(false);
      return;
    }

    const fetchVoucher = async () => {
      try {
        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/villa-voucher?id=${id}`,
          {
            headers: {
              Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            },
          },
        );
        const result = (await response.json()) as VoucherData;

        if (!response.ok || result.error) {
          throw new Error(
            result.error ||
              `Fehler beim Laden der Bestätigung (Status ${response.status})`,
          );
        }

        setData(result);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Die Bestätigung konnte nicht geladen werden.',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchVoucher();
  }, [id]);

  useEffect(() => {
    document.title = 'Buchungs-Bestätigung – Bali Chakra Tour';
    return () => {
      document.title = 'Bali VW Tour';
    };
  }, []);

  const isEn = data?.lang === 'en';

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center text-foreground-500">
        {isEn ? 'Loading confirmation...' : 'Bestätigung wird geladen...'}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center px-4">
        <div className="bg-background-50 p-8 rounded-lg border border-background-200 max-w-md text-center">
          <h2 className="text-xl font-semibold text-foreground-800 mb-2">
            {isEn ? 'Confirmation unavailable' : 'Bestätigung nicht verfügbar'}
          </h2>
          <p className="text-foreground-500">
            {error || (isEn ? 'No data available.' : 'Keine Daten vorhanden.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="voucher-page min-h-screen bg-background-100 py-6 px-4">
      <div className="max-w-3xl mx-auto text-right mb-4 print:hidden">
        <button
          type="button"
          onClick={() => window.print()}
          className="bg-blue-500 text-background-50 px-6 py-2.5 rounded-md text-sm font-semibold hover:bg-blue-600 transition-colors whitespace-nowrap"
        >
          {isEn ? 'Print / save as PDF' : 'Drucken / als PDF speichern'}
        </button>
      </div>

      <style>{PRINT_CSS}</style>
      <div className="voucher-sheet max-w-3xl mx-auto bg-background-50 rounded-lg overflow-hidden border border-blue-100 print:border-none print:rounded-none print:mx-0 print:max-w-full">
        {/* Header */}
        <div className="vh-header bg-gradient-to-br from-blue-700 via-blue-600 to-blue-500 text-white px-7 py-8 text-center">
          <div className="vh-header-gap flex flex-col items-center gap-4">
            <div className="bg-blue-50 rounded-xl px-4 py-3">
              <img
                src={BALI_CHAKRA_LOGO}
                alt="Bali Chakra Tours & Travel Logo"
                className="vh-logo w-80 h-auto object-contain"
              />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold tracking-wide font-heading leading-tight">
                {isEn ? 'Booking Confirmation' : 'Buchungs-Bestätigung'}
              </h1>
              <p className="mt-3 inline-block bg-white/15 border border-white/30 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap">
                {isEn ? 'Booking no.' : 'Buchungs-Nr.'}: {data.booking_no || data.id}
              </p>
            </div>
          </div>
        </div>

        {/* Booking title */}
        <div className="vh-title-block px-8 pt-8 pb-4 bg-gradient-to-b from-blue-50 to-background-50">
          <p className="text-xs font-semibold tracking-widest uppercase text-blue-700 mb-1.5">
            {isEn ? 'Your booking' : 'Ihre Buchung'}
          </p>
          <h2 className="text-2xl md:text-3xl font-semibold text-blue-900 font-heading leading-tight">
            {data.villa_name}
          </h2>
        </div>

        {/* Body */}
        <div className="vh-body px-8 pb-8">
          <p className="text-sm text-foreground-500 mb-6">
            {isEn
              ? 'Thank you for your booking! Please bring this confirmation with you at check-in.'
              : 'Vielen Dank für Ihre Buchung! Bitte bringen Sie diese Bestätigung bei der Anreise mit.'}
          </p>

          <div className="vh-guest rounded p-3.5 mb-6 bg-blue-50 border-l-4 border-blue-500">
            <p className="text-base font-semibold text-foreground-800">
              {data.guest_name}
            </p>
            <p className="text-sm text-foreground-600">
              {data.email}
              {data.phone ? ` · ${data.phone}` : ''}
            </p>
          </div>

          <table className="vh-table w-full text-sm border-collapse">
            <tbody>
              <tr>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500 w-2/5">
                  {isEn ? 'Villa' : 'Villa'}
                </td>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                  {data.villa_name}
                </td>
              </tr>
              {(data.villa_slug === 'villa-frangipani' ||
                data.villa_slug === 'villa-hibiskus') && (
                <tr>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                    {isEn ? 'Room' : 'Zimmer'}
                  </td>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                    {data.rooms}
                  </td>
                </tr>
              )}
              <tr>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                  {isEn ? 'Check-in' : 'Anreise'}
                </td>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                  {data.check_in_display}
                </td>
              </tr>
              <tr>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                  {isEn ? 'Check-out' : 'Abreise'}
                </td>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                  {data.check_out_display}
                </td>
              </tr>
              <tr>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                  {isEn ? 'Number of nights' : 'Anzahl Nächte'}
                </td>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                  {data.nights}
                </td>
              </tr>
              <tr>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                  {isEn ? 'Adults' : 'Erwachsene'}
                </td>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                  {data.adults}
                </td>
              </tr>
              <tr>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                  {isEn ? 'Price per night' : 'Preis pro Nacht'}
                </td>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                  {money(data.price_per_night, !isEn)} ({isEn ? 'incl. breakfast' : 'inkl. Frühstück'})
                </td>
              </tr>
              <tr className="bg-blue-50">
                <td className="py-2.5 px-1.5 font-bold text-blue-900">
                  {isEn ? 'Total price' : 'Gesamtpreis'}
                </td>
                <td className="py-2.5 px-1.5 text-right font-bold text-blue-900">
                  {money(data.total_price, !isEn)} ({isEn ? 'incl. breakfast' : 'inkl. Frühstück'})
                </td>
              </tr>
            </tbody>
          </table>

          <CheckInOutNote isDe={!isEn} className="mt-5" />
        </div>

        {/* Footer */}
        <div className="vh-footer px-8 py-6 bg-blue-50 border-t border-blue-100 text-xs text-foreground-600 leading-relaxed">
          <p className="mb-4 text-blue-800 font-medium">
            {isEn
              ? 'All bookings on the website www.balivwtour.com are managed by our own registered travel company PT. Bali Chakra Tour.'
              : 'Alle Buchungen der Webseite www.balivwtour.com werden über unser eigenes registriertes Reiseunternehmen PT. Bali Chakra Tour verwaltet.'}
          </p>
          <strong className="text-blue-900">Bali Chakra Tours & Travel</strong>
          <br />
          Jalan Ganetri 9 D, Gatot Subroto Timur
          <br />
          Denpasar 80235, Bali, Indonesia
          <br />
          {isEn ? 'Phone' : 'Telefon'}: +62 361 8956561 · WhatsApp: +62 81 246 21 8000
          <br />
          {isEn ? 'Email' : 'E-Mail'}:{' '}
          <a
            href="mailto:office@balichakratours.com"
            className="text-blue-700 no-underline"
          >
            office@balichakratours.com
          </a>{' '}
          ·{' '}
          <a
            href="https://www.balichakratours.com"
            className="text-blue-700 no-underline"
          >
            www.balichakratours.com
          </a>
        </div>
      </div>
    </div>
  );
}